/**
 * InvoiceActions Component
 * Displays action buttons and viewer settings after an invoice is generated
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { InvoiceEditor } from './InvoiceEditor';
import { exportPDFWithReactPDF } from './PDFPreview';
import { calculatePageLayouts } from '@/utils/pagination';
import { AdModal } from '@/components/common/AdModal';
import { logger } from '@/utils/logger';
import { generateInvoiceFilename } from '@/utils/filename';

interface InvoiceActionsProps {
  onNewInvoice: () => void;
}

export function InvoiceActions({ onNewInvoice }: InvoiceActionsProps) {
  const { t } = useTranslation();
  const { currentInvoice, setExporting, totalPages } = useInvoiceStore();
  const [showAd, setShowAd] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Use invoice's paperSize or default
  const effectivePaperSize = currentInvoice?.paperSize || { preset: 'A4' as const, width: 210, height: 297, orientation: 'portrait' as const };

  const handleExport = async (format: 'pdf' | 'png') => {
    if (!currentInvoice) return;

    try {
      setExporting(true);

      const filename = generateInvoiceFilename(currentInvoice);

      // Calculate page layouts for pdfmake
      const pageLayouts = calculatePageLayouts(currentInvoice, effectivePaperSize, totalPages);

      if (format === 'pdf') {
        // Use @react-pdf/renderer for PDF export
        await exportPDFWithReactPDF(currentInvoice, effectivePaperSize, pageLayouts, filename);
      } else {
        // PNG export not available with PDF preview
        alert('PNG export is not available. Please use PDF export.');
        return;
      }

      setShowAd(true);
    } catch (error) {
      logger.error('Export failed:', error);
      alert(error instanceof Error ? error.message : t('export.failed'));
    } finally {
      setExporting(false);
    }
  };

  const isExporting = useInvoiceStore.getState().isExporting;

  return (
    <>
      <div className="space-y-4">
        {/* Inline Ad - Only show when editor is not active TODO: enable later*/}
        {/* {!showEditor && (
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
            <InlineAd />
          </div>
        )} */}

        {/* New Invoice Button */}
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
          <button
            onClick={onNewInvoice}
            className="w-full btn-primary flex items-center justify-center gap-2 py-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('export.createAnother', 'Make New Invoice')}
          </button>
        </div>

        {/* Action Buttons - More Compact */}
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowEditor(!showEditor)}
              className="btn-secondary dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 flex items-center gap-1.5 flex-1 min-w-[100px] py-2 px-3 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              {showEditor ? t('export.hideEditor', 'Hide') : t('export.editInvoice', 'Edit')}
            </button>

            <button
              onClick={() => handleExport('pdf')}
              disabled={isExporting}
              className="btn-primary flex items-center gap-1.5 flex-1 min-w-[100px] py-2 px-3 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download PDF
            </button>
          </div>
        </div>

        {/* Invoice Editor - More Compact */}
        {showEditor && currentInvoice && (
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
            <InvoiceEditor invoice={currentInvoice} compact={true} />
          </div>
        )}
      </div>

      {/* Post-download ad modal */}
      <AdModal
        isOpen={showAd}
        onClose={() => setShowAd(false)}
        title={t('export.downloadedTitle')}
        message={t('export.downloadedMessage')}
        primaryButtonText={t('export.createAnother')}
        secondaryButtonText={t('export.close')}
      />
    </>
  );
}
